import {useEffect, useState} from "react";

import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import EmptyData from "@/components/EmptyData";

import {GpsAPI} from "@/api/gps";
import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";
import {
	GPS_FRACTION_OPTIONS,
	GPS_STAT_FRACTION_LABEL,
	GPS_STAT_METRICS,
	GPS_STAT_TEXT_FIELDS,
	buildGpsStatPayload,
	createGpsStatForm,
} from "@/config/matchGpsStats";

import "./GpsStatsTab.scss";

export default function GpsStatsTab({matchId, players, handleNotification}) {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [form, setForm] = useState(createGpsStatForm());
	const [saving, setSaving] = useState(false);
	const [confirmDeleteId, setConfirmDeleteId] = useState(null);

	const playerOptions = (players || []).map((p) => ({
		value: p.id,
		label: `${p.firstName} ${p.lastName}`,
	}));

	const load = async () => {
		if (!matchId) return;
		setLoading(true);
		try {
			const response = await GpsAPI.list(matchId);
			setRows(response || []);
		} catch {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		load();
	}, [matchId]); // eslint-disable-line react-hooks/exhaustive-deps

	const resolvePlayerName = (r) => {
		const p = (players || []).find((pl) => pl.id === r.playerId) || r.player;
		return p ? `${p.firstName} ${p.lastName}` : "—";
	};

	const openAdd = () => {
		setEditingId(null);
		setForm(createGpsStatForm());
		setModalOpen(true);
	};

	const openEdit = (r) => {
		setEditingId(r.id);
		const f = createGpsStatForm();
		f.player =
			playerOptions.find((o) => o.value === r.playerId) ||
			(r.player
				? {
						value: r.playerId,
						label: `${r.player.firstName} ${r.player.lastName}`,
					}
				: null);
		f.fraction =
			GPS_FRACTION_OPTIONS.find((o) => o.value === r.fraction) ||
			GPS_FRACTION_OPTIONS[0];
		GPS_STAT_TEXT_FIELDS.forEach((t) => (f[t.key] = r[t.key] ?? ""));
		GPS_STAT_METRICS.forEach((m) => (f[m.key] = r[m.key] ?? ""));
		setForm(f);
		setModalOpen(true);
	};

	const save = async () => {
		if (!form.player?.value) {
			handleNotification &&
				handleNotification("Seleziona un giocatore", "error");
			return;
		}
		setSaving(true);
		try {
			const body = buildGpsStatPayload(form, matchId);
			if (editingId) {
				await GpsAPI.update(matchId, editingId, {...body, id: editingId});
			} else {
				await GpsAPI.create(body);
			}
			handleNotification && handleNotification(SUCCESSFUL_OPERATION, "success");
			setModalOpen(false);
			load();
		} catch {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setSaving(false);
		}
	};

	const doDelete = async () => {
		try {
			await GpsAPI.delete(matchId, confirmDeleteId);
			handleNotification && handleNotification(SUCCESSFUL_OPERATION, "success");
			setConfirmDeleteId(null);
			load();
		} catch {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		}
	};

	const updateForm = (key, value) => setForm((f) => ({...f, [key]: value}));

	return (
		<div className="gps-stats-tab">
			<div className="gps-stats-tab__toolbar">
				<span className="gps-stats-tab__count">{rows.length} righe GPS</span>
				<Button
					variant="outlined"
					size="small"
					startIcon={<AddIcon />}
					onClick={openAdd}
				>
					Aggiungi
				</Button>
			</div>

			{!loading && rows.length === 0 && <EmptyData />}

			{rows.length > 0 && (
				<div className="gps-stats-table-wrap">
					<table className="gps-stats-table">
						<thead>
							<tr>
								<th className="sticky-col">Giocatore</th>
								<th>Frazione</th>
								{GPS_STAT_METRICS.map((m) => (
									<th key={m.key} title={m.label}>
										{m.label}
										{m.unit ? ` (${m.unit})` : ""}
									</th>
								))}
								{GPS_STAT_TEXT_FIELDS.map((t) => (
									<th key={t.key}>{t.label}</th>
								))}
								<th className="actions-col" />
							</tr>
						</thead>
						<tbody>
							{rows.map((r) => (
								<tr key={r.id}>
									<td className="sticky-col">{resolvePlayerName(r)}</td>
									<td>{GPS_STAT_FRACTION_LABEL(r.fraction)}</td>
									{GPS_STAT_METRICS.map((m) => (
										<td key={m.key}>{r[m.key] ?? "—"}</td>
									))}
									{GPS_STAT_TEXT_FIELDS.map((t) => (
										<td key={t.key}>{r[t.key] || "—"}</td>
									))}
									<td className="actions-col">
										<div className="gps-stats-table__actions">
											<button
												type="button"
												title="Modifica"
												onClick={() => openEdit(r)}
											>
												<EditIcon sx={{fontSize: 16}} />
											</button>
											<button
												type="button"
												title="Elimina"
												className="danger"
												onClick={() => setConfirmDeleteId(r.id)}
											>
												<DeleteOutlineIcon sx={{fontSize: 16}} />
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{/* Modale inserimento / modifica */}
			{modalOpen && (
				<VLFModal
					maxWidth="md"
					open={modalOpen}
					title={editingId ? "Modifica dato GPS" : "Aggiungi dato GPS"}
					close={() => setModalOpen(false)}
					confirm={save}
					confirmLabel="Salva"
					loading={saving}
					content={
						<div className="row">
							<div className="col-sm-12 col-md-6">
								<VLFSelectNew
									options={playerOptions}
									value={form.player}
									onChange={(v) => updateForm("player", v)}
									label="Giocatore"
									required
									editable={!editingId}
								/>
							</div>
							<div className="col-sm-12 col-md-6">
								<VLFSelectNew
									options={GPS_FRACTION_OPTIONS}
									sorted={false}
									value={form.fraction}
									onChange={(v) => updateForm("fraction", v)}
									label="Frazione"
								/>
							</div>
							{GPS_STAT_TEXT_FIELDS.map((t) => (
								<div key={t.key} className="col-sm-12 col-md-6">
									<VLFTextField
										value={form[t.key] || ""}
										onChange={(e) => updateForm(t.key, e.target.value)}
										label={t.label}
										size="small"
									/>
								</div>
							))}
							{GPS_STAT_METRICS.map((m) => (
								<div key={m.key} className="col-sm-6 col-md-4 col-xl-3">
									<VLFTextField
										type="number"
										value={form[m.key] ?? ""}
										onChange={(e) => updateForm(m.key, e.target.value)}
										label={m.unit ? `${m.label} (${m.unit})` : m.label}
										size="small"
									/>
								</div>
							))}
						</div>
					}
				/>
			)}

			{/* Conferma eliminazione */}
			{confirmDeleteId && (
				<VLFModal
					maxWidth="xs"
					open={!!confirmDeleteId}
					title="Eliminazione"
					close={() => setConfirmDeleteId(null)}
					confirm={doDelete}
					confirmLabel="Elimina"
					content={<div>Vuoi eliminare questi dati GPS?</div>}
				/>
			)}
		</div>
	);
}
